import { V2SocketEvents } from '@flipster/r230522/original/socket';
import { WsV2Message } from '@flipster/r230522/original/types';
import AbstractSocket from '@flipster/socket/AbstractSocket';
import BridgeSocket, { BridgeSocketEvents } from '@flipster/socket/BridgeSocket';

interface R230522AppMetadata {
  mode?: 'subscription';
}

export interface BridgeR230522Events
  extends BridgeSocketEvents<WsV2Message, R230522AppMetadata>, Omit<V2SocketEvents, 'message'> {}
export class BridgeN2FR230522Socket extends AbstractSocket<unknown, BridgeR230522Events> {
  #bridgeSocket: BridgeSocket<WsV2Message, unknown, R230522AppMetadata, unknown, BridgeR230522Events>;
  #converter: R230522N2FConverter;
  #isNormalSub = false;

  constructor(public socketName = 'bridge-n2f-r230522') {
    super();
    this.#bridgeSocket = new BridgeSocket('socket:r230522', { mode: 'subscription' });
    this.#converter = new R230522N2FConverter((data) => this.#bridgeSocket.send(data));
    this.#bridgeSocket.on('connectionError', (error) => this.emit('connectionError', error));
    this.#bridgeSocket.on('metadata', (metadata) => {
      this.#isNormalSub = metadata.mode === 'subscription';
      this.emit('metadata', metadata);
    });
    this.#bridgeSocket.on('stateChanged', (state) => this.emit('stateChanged', state));
    this.#bridgeSocket.on('connected', () => {
      if (this.#isNormalSub) this.#converter.sendInitialSubMsg();
    });
    this.#bridgeSocket.on('message', (data) => this.emit('message', data));
  }

  get rawBridgeSocket() {
    return this.#bridgeSocket;
  }

  connect() {
    this.#bridgeSocket.connect();
  }

  close(): void {
    this.#bridgeSocket.close();
  }

  send(data: unknown): void {
    if (this.#isNormalSub) {
      this.#converter.send(data);
    } else {
      this.#bridgeSocket.send(data);
    }
  }

  get state() {
    return this.#bridgeSocket.state;
  }

  get debugLabel(): string {
    return this.socketName;
  }

  protected flushQueuedMessages(): void {}
  protected clearQueuedMessages(): void {}
}

class R230522N2FConverter {
  #subscriptionManager: SubscriptionManager;
  #currencies: Handle;
  #symbol: Handle;
  #tickers: Handle;
  #intervalData: Handle;
  #orderBook: Handle;

  constructor(private sendFn: (data: unknown) => void) {
    this.#subscriptionManager = new SubscriptionManager((subscriptions) => {
      this.sendFn({ s: subscriptions });
    });
    this.#currencies = this.#subscriptionManager.getHandle(['market/currency-details']);
    this.#symbol = this.#subscriptionManager.getHandle(['market/pswap-details', 'market/spot-details']);
    this.#tickers = this.#subscriptionManager.getHandle(['market/tickers', 'market/spot-tickers']);
    this.#intervalData = this.#subscriptionManager.getHandle(['market/interval-data']);
    this.#orderBook = this.#subscriptionManager.getHandle(['market/orderbooks-v2']);
    const fixedTopics = [
      'private/account',
      'private/margins',
      'market/pswaps',
      'private/orders',
      'private/positions',
      'private/pswaps',
      'market/spots',
      'private/spot-orders',
      'private/spots',
      'private/wallets',
    ];
    this.#subscriptionManager.getHandle(fixedTopics, ['*']);
  }

  set currencies(currencies: string[] | undefined | null) {
    this.#currencies.set(currencies);
  }
  set symbol(symbol: string | undefined | null) {
    this.#symbol.set(symbol ? [symbol] : undefined);
  }
  set tickers(tickers: string | undefined | null) {
    this.#tickers.set(tickers ? [tickers] : undefined);
  }
  set intervalData(intervalData: string[] | undefined | null) {
    this.#intervalData.set(intervalData);
  }
  set orderBook(orderBook: string | undefined | null) {
    this.#orderBook.set(orderBook ? [orderBook] : undefined);
  }

  sendInitialSubMsg() {
    this.sendFn({ s: this.#subscriptionManager.getSubscriptions() });
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  send(data: any): void {
    if (data.s) {
      if ('currencies' in data.s) return (this.currencies = data.s.currencies);
      if ('symbol' in data.s) return (this.symbol = data.s.symbol);
      if ('tickers' in data.s) return (this.tickers = data.s.tickers);
      if ('intervalData' in data.s) return (this.intervalData = data.s.intervalData);
      if ('orderBook' in data.s) return (this.orderBook = data.s.orderBook);
    }
    this.sendFn(data);
  }
}

interface Subscription {
  rows: string[]; // "*" for all rows, or specific row IDs
}

interface Handle {
  set(rows?: string[] | null): void;
}

class SubscriptionManager {
  #batchUpdater: BatchUpdater;
  #subscriptions: Record<string, Set<Subscription>> = {};

  constructor(private updateFn: (subscriptions: Record<string, Subscription>) => void) {
    this.#batchUpdater = new BatchUpdater((topics) => this.updateFn(this.getSubscriptions(topics)));
  }

  get everyTopics() {
    return Object.keys(this.#subscriptions);
  }

  getHandle(topics: string[], initialRows: string[] = []): Handle {
    const subscriptions: Record<string, Subscription> = {};
    for (const topic of topics) {
      const subscription = (subscriptions[topic] = { rows: initialRows });
      this.subscribe(topic, subscription);
    }
    return {
      set: (rows) => {
        for (const topic of topics) {
          const subscription = subscriptions[topic];
          subscription.rows = rows ?? [];
          this.#batchUpdater.touch(topic);
        }
      },
    };
  }

  subscribe(topic: string, subscription: Subscription): () => void {
    this.#subscriptions[topic] ??= new Set();
    this.#subscriptions[topic].add(subscription);
    this.#batchUpdater.touch(topic);
    return () => {
      this.#subscriptions[topic]?.delete(subscription);
      this.#batchUpdater.touch(topic);
    };
  }

  getSubscriptions(topics: string[] = this.everyTopics): Record<string, Subscription> {
    const result: Record<string, Set<string>> = {};
    for (const topic of topics) {
      const resultTopicRows = (result[topic] ??= new Set());
      for (const { rows } of this.#subscriptions[topic] ?? []) {
        for (const row of rows) resultTopicRows.add(row);
      }
    }
    return Object.fromEntries(Object.entries(result).map(([topic, rows]) => [topic, { rows: Array.from(rows) }]));
  }
}

class BatchUpdater {
  #touchedTopics: Set<string> = new Set();
  #timeoutId: NodeJS.Timeout | undefined;

  constructor(private updateFn: (topics: string[]) => void) {}

  touch(topic: string): void {
    this.#touchedTopics.add(topic);
    if (this.#timeoutId) return;
    this.#timeoutId = setTimeout(() => {
      const touchedTopics = Array.from(this.#touchedTopics);
      this.#touchedTopics.clear();
      this.#timeoutId = undefined;
      this.updateFn(touchedTopics);
    });
  }
}
